import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Hammer, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import AboutRelated from "@/components/AboutRelated";

const VALUES = [
  {
    icon: Award,
    title: "Quality, uncompromised",
    body: "Every job is delivered to a standard we would accept on our own equipment. Documentation, finish and reliability — first time, every time.",
  },
  {
    icon: Users,
    title: "Built on partnerships",
    body: "We work hand-in-hand with operations teams. Long-term collaboration is how we add genuine value to your business.",
  },
  {
    icon: Hammer,
    title: "Hands-on engineering",
    body: "From boardroom to shop floor, our engineers solve problems where they happen — not from behind a desk.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance, baked in",
    body: "Externally audited ISO 9001, 14001 and 45001 management systems plus SafeContractor approval — risk-aware practices are part of every project, not an afterthought.",
  },
];

const About = () => (
  <div data-testid="about-page">
    <Seo
      title="About Us"
      description="Founded in 2021, Lutrom Engineering delivers mechanical, electrical and controls engineering to industrial and commercial clients across the UK — with externally audited ISO 9001, 14001 and 45001 management systems."
      path="/about"
    />
    <section className="relative overflow-hidden">
      <PhotoBackdrop src="/gallery/engineer.jpg" position="center 30%" />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-20 lg:pb-28 relative">
        <p className="eyebrow">About Us</p>
        <h1
          className="font-display mt-6 text-white"
          style={{ fontSize: "clamp(44px, 6vw, 88px)", lineHeight: 0.95 }}
          data-testid="about-heading"
        >
          ENGINEERING THAT
          <br />
          <span className="gradient-blue-text">OUTLASTS THE BRIEF.</span>
        </h1>
        <p className="mt-7 max-w-3xl text-[17px] leading-relaxed text-[color:var(--lt-text-dim)]">
          Lutrom Engineering Ltd was founded in 2021 by a team of mechanical,
          electrical and controls engineers. We work across both industrial and
          commercial sectors — from factories and process plants to football
          stadiums, schools, retail stores and the wider commercial estate. From
          hydraulic, pneumatic and water systems to automation, welding, fabrication,
          fire alarms, emergency lighting, machine refurbishment and bespoke design —
          one trusted phone number for every kind of site.
        </p>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <p className="eyebrow">Our story</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(32px, 4vw, 52px)", lineHeight: 0.95 }}
        >
          ONE PHONE NUMBER,
          <br />
          <span className="gradient-blue-text">EVERY KIND OF SITE.</span>
        </h2>
      </div>
      <div className="lg:col-span-7 space-y-5 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
        <p>
          Lutrom was born out of a simple frustration — that too many maintenance
          contracts focused on tickets closed, not assets kept running. We built our
          firm on a different model: small teams, strong engineering, transparent
          reporting and the kind of relationship where you can text the engineer who
          knows your site.
        </p>
        <p>
          Today we look after PPM programmes, EICRs, thermal and vibration surveys,
          PAT testing rounds, electrical installations, hydraulic, pneumatic and
          water systems, automation, welding, fabrication and bespoke machinery —
          for clients across manufacturing, food, process, retail, education,
          sport and the wider commercial estate. All backed by our 24/7 call-out
          promise.
        </p>
        <p>
          Our internal Operations Portal keeps clients in the loop in real-time:
          work orders, certificates, engineer time and reports all in one place —
          a level of visibility most contractors simply don&apos;t offer.
        </p>
      </div>
    </section>

    <section className="relative overflow-hidden mt-24 py-20 lg:py-24" data-testid="values-section">
      <PhotoBackdrop src="/gallery/fabrication-cross.jpg" position="center 45%" dim={1.08} />
      <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
        <p className="eyebrow">What we stand for</p>
        <h2
          className="font-display mt-4 text-white"
          style={{ fontSize: "clamp(30px, 3.8vw, 48px)", lineHeight: 0.98 }}
        >
          FOUR PRINCIPLES, ZERO COMPROMISE.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {VALUES.map((v) => {
            const Icon = v.icon;
            return (
              <div
                key={v.title}
                className="glass-card rounded-xl p-7"
                data-testid={`value-${v.title.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
              >
                <span className="icon-tile">
                  <Icon size={22} />
                </span>
                <h3
                  className="font-display mt-5 text-white"
                  style={{ fontSize: 22, letterSpacing: "0.02em" }}
                >
                  {v.title.toUpperCase()}
                </h3>
                <p className="mt-3 text-sm text-[color:var(--lt-text-dim)] leading-relaxed">
                  {v.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
      <div className="glow-card rounded-2xl p-10 lg:p-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative overflow-hidden">
        <PhotoBackdrop src="/gallery/assembled-taping-unit.jpg" position="center 40%" dim={1.05} />
        <div className="relative">
          <p className="eyebrow">Work with us</p>
          <h3 className="font-display mt-3 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 40px)", lineHeight: 1 }}>
            LET'S TALK ABOUT YOUR SITE.
          </h3>
        </div>
        <Link
          to="/contact"
          className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold self-start md:self-auto relative"
          data-testid="about-cta"
        >
          Get in touch <ArrowRight size={15} />
        </Link>
      </div>
    </section>

    <AboutRelated excludeSlug="" />
  </div>
);

export default About;
